import {
  getCompanyId,
  getCustomerOrderDetailsFromAmalgam,
  getCustomerOrdersFromAmalgam,
  isCustomerOrderAmalgamAvailable
} from './Amalgam';
import { getCompanySelectedLanguage, shouldHideProfilePageSection } from './Experiments';
import {
  fetchCustomerOrders as fetchCustomerOrdersFromRemote,
  fetchCustomerOrderDetails as fetchCustomerOrderDetailsFromRemote,
  downloadCustomerOrderPdf
} from './Api';
import { createQueryParams } from './Request';
import { sendMessageOnWhatsapp } from './Reseller';
import I18n from './I18n';
import { PATH } from './Route';
import {
  isAppRunningLocallyOrRootDomain,
  isDesktopView,
  isResellerBundleView,
  QUICKSELL_WHATSAPP
} from './Utils';

let _cachedCustomerOrders = null;
const _cachedCustomerOrderDetails = {};
let loadNextOrdersPromise = null;
const customerOrderDetailsPromises = {};

export const shouldShowCustomerAccountMenu = () =>
  !isDesktopView() && !shouldHideProfilePageSection();

export const getCustomerOrderPortalRoute = (orderId) => {
  let queryParams = null;

  // will help user to debug on localhost or without custom or catalog.to domain
  if (isAppRunningLocallyOrRootDomain()) {
    queryParams = [{ key: 'companyId', value: getCompanyId() }];
  }

  return `${
    orderId ? PATH.CUSTOMER_ORDER_DETAILS.replace(':orderId', orderId) : PATH.CUSTOMER_ORDERS
  }${createQueryParams(queryParams)}`;
};

export const getOrderDisplayIdFromOrdersList = (orderId) => {
  if (!orderId) {
    return;
  }

  const { orders } = getCachedCustomerOrders() || {};

  if (!Array.isArray(orders)) {
    return;
  }

  const { displayOrderId } = orders.find(({ orderId: id }) => id === orderId) || {};

  return displayOrderId;
};

export const getCustomerOrderPageTitle = ({ orderId, displayOrderId } = {}) => {
  const displayIdForSelectedOrder = displayOrderId || getOrderDisplayIdFromOrdersList(orderId);
  if (displayIdForSelectedOrder) {
    const id = `#${displayIdForSelectedOrder}`;
    return I18n.t('ORDER_DISPLAY_ID', { id }) || `Order ${id}`;
  }

  return I18n.t('ORDERS');
};

export const getCachedCustomerOrders = () => _cachedCustomerOrders;

export const setCachedCustomerOrders = (orders) => {
  _cachedCustomerOrders = orders;
  window.amalgamObject.orders = orders;
};

export const getCachedCustomerOrdersDetails = (orderId) =>
  _cachedCustomerOrderDetails[orderId] || null;

export const setCachedCustomerOrdersDetails = (orderId, orderDetails) => {
  _cachedCustomerOrderDetails[orderId] = orderDetails;
};

export const isNextOrdersPageAvailable = (ordersResponse = getCachedCustomerOrders()) => {
  const { orders, count } = ordersResponse || {};

  return Array.isArray(orders) && orders.length < count;
};

export const downloadCustomerOrderPdfHandler = ({ orderId, visitorId }) =>
  downloadCustomerOrderPdf({ orderId, visitorId }).then((pdfDetails) => {
    if (!pdfDetails) {
      throw new Error('Details not found');
    }

    const downloadPdfTag = document.createElement('a');
    downloadPdfTag.href = pdfDetails.pdfLink;
    downloadPdfTag.download = pdfDetails.fileName;
    downloadPdfTag.click();

    return Promise.resolve(pdfDetails);
  });

export const onReceiveOrderByWhatsappClick = ({ orderId, fromBundle }) => {
  const phone = QUICKSELL_WHATSAPP;
  const message = `Order ID : ${orderId}`;
  if (isResellerBundleView(fromBundle)) {
    sendMessageOnWhatsapp({ message, phone });
    return;
  }
  const language = getCompanySelectedLanguage();
  window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${message}&lang=${language}`);
};

const fetchCustomerOrders = () =>
  fetchCustomerOrdersFromRemote().then((ordersResponse) => {
    setCachedCustomerOrders(ordersResponse);
    return Promise.resolve(ordersResponse);
  });

const fetchNextCustomerOrders = ({ pageData }) => {
  if (loadNextOrdersPromise) {
    return loadNextOrdersPromise;
  }

  loadNextOrdersPromise = fetchCustomerOrdersFromRemote({ pageData })
    .then((nextOrdersResponse) => {
      const { orders: prevOrders, count } = getCachedCustomerOrders();

      let newOrdersList = prevOrders;
      const { orders: nextOrders, nextPageData } = nextOrdersResponse || {};

      loadNextOrdersPromise = null;

      if (nextOrdersResponse) {
        if (Array.isArray(nextOrders) && nextOrders.length) {
          newOrdersList = [...prevOrders, ...nextOrders];

          const newOrderResponse = { orders: newOrdersList, count, nextPageData };

          setCachedCustomerOrders(newOrderResponse);

          return Promise.resolve(newOrderResponse);
        }
      }

      return Promise.resolve(null);
    })
    .catch((error) => {
      loadNextOrdersPromise = null;
      const { orders, nextPageData } = getCachedCustomerOrders();
      setCachedCustomerOrders({ orders, count: (orders || []).length, nextPageData });
      throw error;
    });

  return loadNextOrdersPromise;
};

const fetchCustomerOrderDetails = (orderId) => {
  if (customerOrderDetailsPromises[orderId]) {
    return customerOrderDetailsPromises[orderId];
  }

  const orderDetailsPromise = fetchCustomerOrderDetailsFromRemote({ orderId }).then(
    (orderDetailsResponse) => {
      setCachedCustomerOrdersDetails(orderId, orderDetailsResponse);
      delete customerOrderDetailsPromises[orderId];
      return Promise.resolve(orderDetailsResponse);
    }
  );

  customerOrderDetailsPromises[orderId] = orderDetailsPromise;
  return orderDetailsPromise;
};

export const getCustomerOrders = () => {
  const cachedOrders = getCachedCustomerOrders();
  if (cachedOrders) {
    return Promise.resolve(cachedOrders);
  }

  if (isCustomerOrderAmalgamAvailable()) {
    const orders = getCustomerOrdersFromAmalgam();
    if (orders) {
      setCachedCustomerOrders(orders);
      return Promise.resolve(orders);
    }
  }

  return fetchCustomerOrders();
};

export const loadMoreCustomerOrders = () => {
  const cachedOrders = getCachedCustomerOrders();
  const { orders, count, nextPageData } = cachedOrders || {};

  if (!orders || count === 0 || orders.length >= count) {
    return Promise.resolve(null);
  }

  if (!Array.isArray(orders) || !orders.length) {
    return fetchCustomerOrders();
  }

  return fetchNextCustomerOrders({ pageData: nextPageData });
};

export const getCustomerOrderDetails = (orderId) => {
  const cachedOrderDetails = getCachedCustomerOrdersDetails(orderId);

  if (cachedOrderDetails) {
    return Promise.resolve(cachedOrderDetails);
  }

  if (isCustomerOrderAmalgamAvailable()) {
    const orderDetails = getCustomerOrderDetailsFromAmalgam(orderId);
    if (orderDetails) {
      setCachedCustomerOrdersDetails(orderId, orderDetails);
      return Promise.resolve(orderDetails);
    }
  }

  return fetchCustomerOrderDetails(orderId);
};
